import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { NgbModal, NgbOffcanvas, NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLinkButton, NgbNavLinkBase, NgbNavContent, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap';
import { ToastService } from 'src/app/services/toasts.service';
import { Options, NgxSliderModule } from '@angular-slider/ngx-slider';
import { OverlayService } from 'src/app/services/overlay.service';
import { PlanogramApiService } from 'src/app/services/planogram-api.service';
import { LayoutMetricsService } from 'src/app/services/layout-metrics.service';
import { RolesService } from 'src/app/services/roles.service';
import { SystemRole } from 'src/app/models/roles';
import { map, Observable } from 'rxjs';
import { DosSelectModalComponent } from 'src/app/modals/dos-select-modal/dos-select-modal.component';
import { PlanogramLevel } from 'src/app/models/planogram-level';
import { PlanogramService } from 'src/app/services/planogram.service';
import { CapabilityType } from 'src/app/models/capability-type';
import { ApprovePlanogramModalComponent } from 'src/app/modals/approve-planogram-modal/approve-planogram-modal.component';
import { FormsModule } from '@angular/forms';
import { PlanogramLevelsComponent } from '../../planogram-levels/planogram-levels.component';
import { NgIf } from '@angular/common';
import { HasCapabilityDirective } from '../../../directives/has-capability.directive';

@Component({
    selector: 'planogram-options',
    templateUrl: './planogram-options.component.html',
    styleUrls: ['./planogram-options.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLinkButton,
        NgbNavLinkBase,
        NgbNavContent,
        FormsModule,
        PlanogramLevelsComponent,
        NgIf,
        NgxSliderModule,
        NgbNavOutlet,
        HasCapabilityDirective,
    ],
})
export class PlanogramOptionsComponent implements OnInit {
  CapabilityType = CapabilityType;
  levels: PlanogramLevel[] = [];
  
  // Drop Settings
  dropHeight: number = 195;
  dropDepth: number = 38;
  shelfWidth: number = 91;
  footerHeight: number = 20;
  shelfLipHeight: number = 4;
  shelfMinSpacing: number = 2;
  targetDrops: number | null = null;
  
  // Planogram Settings
  geneticAlgorithmOptimization: boolean = true;
  applyOptimization: boolean = false;
  maxStackOptimization: boolean = false; 
  refinementEngineEnabled: boolean = false;
  refinementIterations: number = 10;
  resetFacingsToMinDOS: boolean = false;
  forceMaxStack: boolean = false;
  dosMin: number = 7;
  dosMax: number = 14;
  dosSliderOptions: Options = {
    floor: 1,
    ceil: 90,
    step: 1,
    minRange: 1,
    showSelectionBar: true,
    showOuterSelectionBars: true
  };

  // CPI threshold properties
  cpiTier1: number = 200; // Red threshold
  cpiTier2: number = 400; // Orange threshold
  cpiTier3: number = 600; // Yellow threshold

  // Tab control
  activeTab: string = 'product-ordering';

  constructor(
    public planogramService: PlanogramService,
    private planogramApiService: PlanogramApiService,
    private offcanvasService: NgbOffcanvas,
    private toastService: ToastService,
    private overlayService: OverlayService,
    public rolesService: RolesService,
    private modalService: NgbModal,
    private layoutMetricsService: LayoutMetricsService
  ) { }

  ngOnInit(): void {
    this.levels = this.planogramService.getLevels();
    this.dropHeight = this.planogramService.getDropHeight();
    this.dropDepth = this.planogramService.getDropDepth();
    this.shelfWidth = this.planogramService.getShelfWidth();
    this.footerHeight = this.planogramService.getDropFooterHeight();

    let applyOptimization = this.planogramService.getSetting(
      'apply-optimization'
    );
    if (applyOptimization == null) {
      this.applyOptimization = true;
    } else {
      this.applyOptimization =
        applyOptimization == 'true' || applyOptimization == true;
    }
    const maxStackOpt = this.planogramService.getSetting('max-stack-optimization');
    this.maxStackOptimization = maxStackOpt == 'true' || maxStackOpt === true;
    let resetFacings = this.planogramService.getSetting(
      'reset-facings-to-min-dos'
    );
    this.resetFacingsToMinDOS = resetFacings == 'true' || resetFacings == true;
    const forceMaxStackSetting = this.planogramService.getSetting('force-max-stack');
    this.forceMaxStack = forceMaxStackSetting == 'true' || forceMaxStackSetting === true;
    this.dosMin = (this.planogramService.getSetting('dos-min') as number) || 7;
    this.dosMax = (this.planogramService.getSetting('dos-max') as number) || 14;
    if(this.dosMax > 90){
      this.dosSliderOptions = {
        ...this.dosSliderOptions,
        ceil: this.dosMax
      };
    }
    this.shelfLipHeight = this.planogramService.getShelfLipHeight();
    this.shelfMinSpacing = this.planogramService.getShelfMinimumSpacing();
    this.cpiTier1 = this.planogramService.getSetting('cpi-tier1') || 200;
    this.cpiTier2 = this.planogramService.getSetting('cpi-tier2') || 400;
    this.cpiTier3 = this.planogramService.getSetting('cpi-tier3') || 600;
    const customTargetDrops = this.planogramService.getSetting('target-drops');
    const parsedTargetDrops = customTargetDrops !== null ? Number(customTargetDrops) : null;
    // Number("null"), Number("") and other non-numeric persisted strings yield NaN; treat as unset.
    this.targetDrops =
      parsedTargetDrops !== null && Number.isFinite(parsedTargetDrops) ? parsedTargetDrops : null;
  }

  openDosOverride() {
  this.modalService.open(DosSelectModalComponent, {
    size: 'md',
    backdrop: 'static',
    centered: true,
  }).result.then((result) => {
    if (result && result.dosMin !== undefined && result.dosMax !== undefined) {
      const newCeil = result.dosMax >= 90 ? result.dosMax : 90;

      this.dosSliderOptions = {
        ...this.dosSliderOptions,
        ceil: newCeil
      };

      this.dosMin = result.dosMin;
      this.dosMax = result.dosMax;
    }
  }).catch(() => {});
}

  setOrderingDirection(level: PlanogramLevel, updateEvent: Event) {
    if (updateEvent == null || typeof event == 'undefined') {
      return;
    }
    const selectedOption: number = parseInt(
      (event.target as HTMLSelectElement).value
    );
    for (let i = 0; i < this.levels.length; i++) {
      if (this.levels[i].type == level.type) {
        this.levels[i].orderingDirection = selectedOption;
        break;
      }
    }
  }

  setOrderingMethod(level: PlanogramLevel, updateEvent: Event) {
    if (updateEvent == null || typeof event == 'undefined') {
      return;
    }
    const selectedOption: number = parseInt(
      (event.target as HTMLSelectElement).value
    );
    for (let i = 0; i < this.levels.length; i++) {
      if (this.levels[i].type == level.type) {
        this.levels[i].orderingMethod = selectedOption;
        break;
      }
    }
  }

  onRefinementIterationsInput(event: any): void {
    const value = event.target.value;
    if (value.includes('.')) {
      this.refinementIterations = Math.floor(this.refinementIterations);
    }
  }

  onDropHeightInput(event: any): void {
    const value = event.target.value;
    if (value.includes('.')) {
      this.dropHeight = Math.floor(this.dropHeight);
    }
  }
  
  onDropDepthInput(event: any): void {
    const value = event.target.value;
    if (value.includes('.')) {
      this.dropDepth = Math.floor(this.dropDepth);
    }
  }

  onFooterHeightInput(event: any): void {
    const value = event.target.value;
    if (value.includes('.')) {
      this.footerHeight = Math.floor(this.footerHeight);
    }
  }

  onShelfWidthInput(event: any): void {
    const value = event.target.value;
    if (value.includes('.')) {
      this.shelfWidth = Math.floor(this.shelfWidth);
    }
  }
  onShelfLipHeightInput(event: any): void {
    const value = event.target.value;
    if (value.includes('.')) {
      this.shelfLipHeight = Math.floor(this.shelfLipHeight);
    }
  }

  onShelfMinSpacingInput(event: any): void {
    const value = event.target.value;
    if (value.includes('.')) {
      this.shelfMinSpacing = Math.floor(this.shelfMinSpacing);
    }
  }

  clearTargetDropsSetting() {
    this.targetDrops = null;
    this.planogramService.setSetting('target-drops', null);
  }

  saveDefaultSettings() {
    this.planogramApiService
      .updateMerchDefaultLevels(this.planogramService.getMerchId(), this.levels)
      .subscribe({
        next: () => {
          this.planogramService.setLevels(this.levels);
          this.offcanvasService.dismiss();
        },
        error: (error) =>
          console.error('Error saving default settings:', error),
      });
  }

  saveSettings() {
    this.planogramService.setLevels(this.levels);
    this.planogramService.setDropHeight(this.dropHeight);
    this.planogramService.setDropDepth(this.dropDepth);
    this.planogramService.setDropFooterHeight(this.footerHeight);
    this.planogramService.setShelfWidth(this.shelfWidth);
    this.planogramService.setShelfLipHeight(this.shelfLipHeight);
    this.planogramService.setShelfMinimumSpacing(this.shelfMinSpacing);
    if (this.targetDrops === null || !Number.isFinite(this.targetDrops)) {
      this.planogramService.setSetting('target-drops', null);
    } else {
      this.planogramService.setSetting('target-drops', this.targetDrops);
    }
    this.planogramService.setSetting(
      'apply-optimization',
      this.applyOptimization
    );
    this.planogramService.setSetting(
      'max-stack-optimization',
      this.applyOptimization && this.maxStackOptimization
    );
    this.planogramService.setSetting(
      'reset-facings-to-min-dos',
      this.resetFacingsToMinDOS
    );
     this.planogramService.setSetting('force-max-stack', this.forceMaxStack);
    this.planogramService.setSetting('dos-min', this.dosMin);
    this.planogramService.setSetting('dos-max', this.dosMax);
    this.planogramService.setSetting('cpi-tier1', this.cpiTier1);
    this.planogramService.setSetting('cpi-tier2', this.cpiTier2);
    this.planogramService.setSetting('cpi-tier3', this.cpiTier3);

    this.planogramApiService.updatePlanogramSettings().subscribe({
      next: (success) => {
        if (success) {
          this.toastService.show('Updated planogram settings', {
            classname: `bg-dark text-light p-3 fs-5`,
            delay: 2000,
          });
          this.overlayService.refreshLegendMap();
          // Depth change affects depth-derived metrics (DOS and d-facings)
          // but not horizontal/vertical facings, so recompute and re-publish
          // those breakdowns against the current layout.
          this.layoutMetricsService.updateLayoutDOSBreakdown();
          this.layoutMetricsService.updateLayoutDFacingsBreakdown();
          this.layoutMetricsService.updateLayoutCapacityBreakdown();
        }
        this.offcanvasService.dismiss();
      },
    });
  }

  approvePlanogram() {
    this.modalService.open(ApprovePlanogramModalComponent, {
      size: 'lg',
      backdrop: 'static',
      centered: true,
    }).result.then((approved) => {
      if (approved) {
        this.offcanvasService.dismiss();
      }
    }).catch(() => {});
  }

  cancel() {
    this.offcanvasService.dismiss();
  }
}
